import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Typography from '@mui/material/Typography';

import { TwInputTypes } from './../../components/TwFields/TwFields';
import { TwAutocompleteOption } from './../../components/TwAutocomplete/TwAutocomplete';
import { DriverAlertStyled, EditPencil, TwFieldCustom } from './TwDriverPayCalculator.styles';
import CalculationBox from './CalculationBox/CalculationBox';

import { LOAD_TYPE } from '../../redux/api/load/load.model';
import TwDialog from '../TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import InfoIcon from '@mui/icons-material/Info';
import NumberUtils from '../../utils/numberUtils';
import { SliceOrderRate, SliceTrip } from '../../redux/features/orders/ordersSlice';
import { useAppSelector } from '../../redux/hooks';
import TwGrossPayTooltip from '../TwGrossPayTooltip/TwGrossPayTooltip';
import Stack from '@mui/material/Stack';
import useAuth from '../../providers/Auth/Auth.provider';
import { useGetInvoiceServicesQuery } from '../../redux/api/invoiceService/invoiceServiceApi';
import { InvoiceService } from '../../redux/api/invoiceService/invoiceService.model';

import DriverPayUtils from '../../utils/driverPayUtils';
import { OtherPayErrors } from './OtherLoadPay/OtherLoadPay';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import { ContactFormDriver } from '../../pages/CreateOrder/Dispatch/DispatchInfo';
import {
  TRIP_DRIVER_PAY_UNIT,
  TripDriver,
  TripDriverOtherPay,
  TripDriverOverrideData
} from '../../redux/api/trips/trips.model';
import { EntityType } from '../../redux/Global.model';
import TwDriverPayRateTypeSelector from '../TwDriverPayRateTypeSelector/TwDriverPayRateTypeSelector';
import DriverUtils from '../../utils/driver/driverUtils';

export interface TwDriverPayCalculatorProps {
  driver: ContactFormDriver;
  loadedMiles?: number;
  totalLoadedMiles: number;
  onGrossPayEstimateDataSave: (grossPayEstimateData: TripDriverOverrideData) => void;
  onOtherLoadPayItemsSave: (otherLoadPayItems: TripDriverOtherPay[]) => void;
  handleOpenEditDriver: () => void;
  /**
   * In case of split load we need to pass the segment so we can recalculate loaded miles
   */
  splitLoadSegment?: SliceTrip;
  driversNumber: number;
  otherLoadPayItems?: TripDriverOtherPay[];
  entity: EntityType;
  tripLevelLoadedMiles?: number;
  tripLevelEmptyMiles?: number;
}

/**
 *
 * This component works for both single and team drivers ( 2 drivers only)
 * for normal and split loads
 */

const TwDriverPayCalculator: React.FC<TwDriverPayCalculatorProps> = ({
  driver,
  loadedMiles,
  totalLoadedMiles,
  splitLoadSegment,
  driversNumber,
  entity,
  onGrossPayEstimateDataSave,
  onOtherLoadPayItemsSave,
  handleOpenEditDriver,
  tripLevelLoadedMiles,
  tripLevelEmptyMiles
}) => {
  const { addNotification } = useNotifications();

  const {
    data: { userData },
    getTenantSetting
  } = useAuth();

  const isDriverPayPercentageBasedOnDefaultService = getTenantSetting(
    'driver-pay-percentage-based-on-default-service'
  );

  const [openEstimateModal, setOpenEstimateModal] = useState<boolean>(false);
  const [grossPayEstimateInfo, setGrossPayEstimateInfo] = useState<
    TripDriverOverrideData | undefined
  >(undefined);

  // Other pay items
  const [otherLoadPayItemsInfo, setOtherLoadPayItemsInfo] = useState<TripDriverOtherPay[]>([]);
  const [otherPayItemsErrors, setOtherPayItemsErrors] = useState<OtherPayErrors[]>([]);

  const [calculate, setCalculate] = useState<boolean>(false);
  const [totalDriverPayEstimate, setTotalDriverPayEstimate] = useState<number>(0);
  const [resetValues, setResetValues] = useState<boolean>(false);
  const {
    rate: stateRate,
    order_type: load_type,
    order_rates: load_rates,
    show_advanced_rates,
    selected_division,
    quickbooks_enabled: quickbooksEnabled
  } = useAppSelector((state) => state.orders);

  const driverAutocomplete =
    ((driver.autocompleteDriver as TwAutocompleteOption)?.value as TripDriver) || null;

  const { data: invoiceServicesData = { content: [], total: 0 } } = useGetInvoiceServicesQuery({
    filters: {
      divisionId: selected_division || userData!.defaultDivision.id
    }
  });

  const defaultService = invoiceServicesData.content.find((item) => {
    if ((item as InvoiceService)?.isDefault) {
      return item;
    }
  });

  /**
   * Determines if the driver pay is percentage based on default service
   * @returns Override load rate if the driver pay is percentage based on default service, and service id
   */
  const determinePercentageCalculation = (
    isPercentageCalculation = false
  ): {
    overridenLoadRate: number | undefined;
    qboServiceId: string | null;
    invoiceServiceId: string | null;
  } => {
    let overridenLoadRate = show_advanced_rates ? totalLoadRates : stateRate;
    let qboServiceId = null;
    let invoiceServiceId = null;

    if (
      isDriverPayPercentageBasedOnDefaultService &&
      (driver.driver_pay_override_data?.unit === 'PERCENTAGE' ||
        driverAutocomplete?.default_pay_rate_type === 'PERCENTAGE' ||
        isPercentageCalculation)
    ) {
      /* Find default service rate among all load rates added to the load and save its amount and service id */
      let defaultRateForPercentageCalculation: SliceOrderRate[];

      if (quickbooksEnabled) {
        defaultRateForPercentageCalculation = load_rates.filter(
          (rate) => rate.qbo_service_id === defaultService?.id
        );
        // if show_advanced_rates is false, we need to use the state rate in the driver pay calculation
        if (!show_advanced_rates && defaultRateForPercentageCalculation.length === 1) {
          defaultRateForPercentageCalculation = [
            {
              ...defaultRateForPercentageCalculation[0],
              ui_id: '',
              id: defaultService?.id || '',
              amount: stateRate,
              quantity: 0
            }
          ];
        } else if (!show_advanced_rates && defaultRateForPercentageCalculation.length === 0) {
          defaultRateForPercentageCalculation = [
            {
              ui_id: '',
              id: defaultService?.id || '',
              amount: stateRate,
              quantity: 0
            }
          ];
        }

        qboServiceId = defaultService?.id || null;
      } else {
        defaultRateForPercentageCalculation = show_advanced_rates
          ? load_rates.filter((rate) => rate.invoice_service_id === defaultService?.id)
          : [
              {
                ui_id: '',
                id: defaultService?.id || '',
                amount: stateRate,
                quantity: 0
              }
            ];

        invoiceServiceId = defaultService?.id || null;
      }

      if (defaultRateForPercentageCalculation.length > 0) {
        // Save the amount to use for the calculation
        const amountToUse = defaultRateForPercentageCalculation.reduce((acc, row) => {
          if (row.amount) {
            return acc + (row.amount || 0);
          } else {
            return acc + (row.quantity || 0) * (row.rate || 0);
          }
        }, 0);

        overridenLoadRate = amountToUse;
      } else {
        overridenLoadRate = 0;
        qboServiceId = null;
        invoiceServiceId = null;
      }
    }

    return { overridenLoadRate: overridenLoadRate, qboServiceId, invoiceServiceId };
  };

  const totalLoadRates = useMemo<number>(() => {
    return load_rates.reduce((acc, row) => {
      if (row.amount) {
        return acc + (row.amount || 0);
      } else {
        return acc + (row.quantity || 0) * (row.rate || 0);
      }
    }, 0);
  }, [
    JSON.stringify(load_rates.map((row) => row.amount)),
    JSON.stringify(load_rates.map((row) => row.rate)),
    JSON.stringify(load_rates.map((row) => row.quantity))
  ]);

  const resetEstimateValues = (): void => {
    const recalculateValues = recalculateWithDefaultDriverPayRate();
    setGrossPayEstimateInfo(recalculateValues);
    setResetValues(true);
  };

  const closeModal = (): void => {
    setOpenEstimateModal(false);
  };

  const createInitialValues = (): TripDriverOverrideData => {
    let initialGrossPayData = {} as TripDriverOverrideData;

    const { overridenLoadRate, qboServiceId, invoiceServiceId } = determinePercentageCalculation();

    if (driver.driver_pay_override_data) {
      initialGrossPayData = {
        unit: driver.driver_pay_override_data.unit,
        rate: driver.driver_pay_override_data.rate,
        loaded_miles: driver.driver_pay_override_data.loaded_miles,
        total_loaded_miles: driver.driver_pay_override_data.total_loaded_miles,
        overriden_order_rate: driver.driver_pay_override_data.overriden_order_rate,
        trip_revenue_percentage: driver.driver_pay_override_data.trip_revenue_percentage,
        quantity: driver.driver_pay_override_data.quantity,
        gross_pay_estimate: driver.driver_pay_override_data.gross_pay_estimate,
        empty_miles: driver.driver_pay_override_data.empty_miles,
        qbo_service_id: driver.driver_pay_override_data.qbo_service_id,
        invoice_service_id: driver.driver_pay_override_data.invoice_service_id,
        driver_id: driver.id || '',
        pay_type: driver.driver_pay_override_data.pay_type
      };

      if (
        !isDriverPayPercentageBasedOnDefaultService &&
        (driver.driver_pay_override_data.qbo_service_id ||
          driver.driver_pay_override_data.invoice_service_id)
      ) {
        initialGrossPayData.overriden_order_rate = 0;
        initialGrossPayData.gross_pay_estimate = undefined;
        initialGrossPayData.qbo_service_id = undefined;
        initialGrossPayData.invoice_service_id = undefined;
      }
    } else {
      initialGrossPayData = {
        unit:
          (driverAutocomplete.default_pay_rate_type as TRIP_DRIVER_PAY_UNIT) ||
          TRIP_DRIVER_PAY_UNIT.PER_MILE
      };

      initialGrossPayData.rate = driverAutocomplete.pay_rate ? driverAutocomplete.pay_rate : 0;
      initialGrossPayData.loaded_miles = NumberUtils.roundNumber(
        Number(loadedMiles || 0) / driversNumber
      );
      initialGrossPayData.total_loaded_miles = totalLoadedMiles;
      initialGrossPayData.overriden_order_rate = overridenLoadRate || 0;

      initialGrossPayData.trip_revenue_percentage =
        load_type === LOAD_TYPE.SIMPLE
          ? 100
          : Number(((Number(loadedMiles || 0) * 100) / totalLoadedMiles).toFixed(2) || 0);

      initialGrossPayData.qbo_service_id = qboServiceId;
      initialGrossPayData.invoice_service_id = invoiceServiceId;
      initialGrossPayData.empty_miles = 0;
    }

    return initialGrossPayData;
  };

  const createOtherLoadPayItems = (): TripDriverOtherPay[] => {
    const initialOtherLoadPay: TripDriverOtherPay[] = [];
    if (driver.trip_driver_pay_other_payment && driver.trip_driver_pay_other_payment.length > 0) {
      // We use the existing values if any
      driver.trip_driver_pay_other_payment.forEach((item) => {
        initialOtherLoadPay.push(item);
      });
    } else if (
      driverAutocomplete.default_load_driver_other_pay &&
      driverAutocomplete.default_load_driver_other_pay.length > 0
    ) {
      // If there are default values for the driver, we use those first
      driverAutocomplete.default_load_driver_other_pay.forEach((item) => {
        if (item.is_default) {
          initialOtherLoadPay.push({
            ...item,
            pay_type: item.pay_type
          });
        }
      });
    }

    return initialOtherLoadPay;
  };

  const recalculateWithDefaultDriverPayRate = (): TripDriverOverrideData => {
    const { overridenLoadRate, qboServiceId, invoiceServiceId } = determinePercentageCalculation();
    const rateToUse = overridenLoadRate;

    return {
      unit:
        (driverAutocomplete.default_pay_rate_type as TRIP_DRIVER_PAY_UNIT) ||
        TRIP_DRIVER_PAY_UNIT.PER_MILE,
      driver_id: driver.id || '',
      rate: driverAutocomplete.pay_rate ? driverAutocomplete.pay_rate : 0,
      loaded_miles:
        driverAutocomplete.default_pay_rate_type === 'PER_MILE' ||
        !driverAutocomplete.default_pay_rate_type
          ? NumberUtils.roundNumber((tripLevelLoadedMiles || loadedMiles || 0) / driversNumber)
          : NumberUtils.roundNumber(tripLevelLoadedMiles || loadedMiles || 0),
      total_loaded_miles: totalLoadedMiles,
      overriden_order_rate:
        load_type === LOAD_TYPE.SIMPLE ? (rateToUse || 0) / driversNumber : rateToUse,
      trip_revenue_percentage:
        load_type === LOAD_TYPE.SIMPLE
          ? 100
          : Number(((Number(loadedMiles || 0) * 100) / totalLoadedMiles).toFixed(2) || 0),

      quantity: 0,
      gross_pay_estimate: 0,
      empty_miles: 0,
      qbo_service_id: qboServiceId,
      invoice_service_id: invoiceServiceId
    };
  };

  const getOtherPayItemsMissingFields = useCallback(() => {
    let errors = [];

    // Make sure all item properties are filled in and return the item index and its missing fields
    for (let i = 0; i < otherLoadPayItemsInfo.length; i++) {
      const item = otherLoadPayItemsInfo[i];
      const missingFields: string[] = [];
      if (!item.pay_type) missingFields.push('Pay type');
      if (!item.unit) missingFields.push('Unit');
      if (!item.quantity && item.unit !== ('FLAT' as TRIP_DRIVER_PAY_UNIT))
        missingFields.push('Quantity');
      if (!item.description) missingFields.push('Description');
      if (!item.rate) missingFields.push('Rate');

      if (missingFields.length > 0) {
        errors.push({ itemIndex: i, missingFields });
      } else {
        errors = [];
      }
    }

    setOtherPayItemsErrors(errors);
    return errors;
  }, [
    JSON.stringify(otherLoadPayItemsInfo.map((row) => row.pay_type)),
    JSON.stringify(otherLoadPayItemsInfo.map((row) => row.unit)),
    JSON.stringify(otherLoadPayItemsInfo.map((row) => row.quantity)),
    JSON.stringify(otherLoadPayItemsInfo.map((row) => row.description)),
    JSON.stringify(otherLoadPayItemsInfo.map((row) => row.rate))
  ]);

  const handleSaveEstimate = (): void => {
    // Check for Other pay items errors here and prevent saving if any
    const otherPayErrors = getOtherPayItemsMissingFields();

    if (otherPayErrors.length > 0) {
      addNotification({
        message: 'Please fill in all required fields for other pay items.',
        variant: 'error'
      });
      return;
    }

    if (grossPayEstimateInfo) {
      onGrossPayEstimateDataSave(grossPayEstimateInfo); // this saves to parent
    }

    if (otherLoadPayItemsInfo) {
      onOtherLoadPayItemsSave(otherLoadPayItemsInfo);
    }

    closeModal(); // close and clear local state
  };

  const openEditDriverFromModal = (): void => {
    closeModal();
    handleOpenEditDriver();
  };

  const handleGrossPayCalculation = (): void => {
    const values = createInitialValues();
    const otherLoadPayItems = createOtherLoadPayItems();

    if (values.unit) {
      let grossPay = 0;
      const {
        rate,
        loaded_miles,
        trip_revenue_percentage,
        overriden_order_rate,
        quantity,

        empty_miles
      } = values;
      const payRate = rate || 0;
      const miles = loaded_miles || 0;
      const loadRate = overriden_order_rate || 0;
      const segmentPercentage = trip_revenue_percentage || 0;

      switch (values?.unit) {
        case 'PER_MILE':
          grossPay = payRate * (miles + (empty_miles || 0));
          break;
        case 'PERCENTAGE':
          if (load_type === LOAD_TYPE.SEGMENT) {
            grossPay = (payRate / 100) * (((segmentPercentage / 100) * loadRate) / driversNumber);
          } else {
            grossPay = (payRate / 100) * (loadRate / driversNumber);
          }

          break;
        case 'PER_HOUR':
          grossPay = payRate * (quantity || 0);
          break;
        case 'FLAT':
          grossPay = Number(payRate);
          break;

        default:
          grossPay = payRate * (miles + (empty_miles || 0));
      }

      const calculatedGrossPay = {
        ...values,
        quantity: quantity || 0,
        rate: payRate,
        gross_pay_estimate: Number(grossPay.toFixed(2)) || 0
      };

      setOtherLoadPayItemsInfo(otherLoadPayItems);
      setGrossPayEstimateInfo(calculatedGrossPay);

      onOtherLoadPayItemsSave(otherLoadPayItems);
      onGrossPayEstimateDataSave(calculatedGrossPay);
    }
  };

  const handleOpenCalculatorModal = (): void => {
    const grossPayValues = createInitialValues();
    const otherPayValues = createOtherLoadPayItems();
    setGrossPayEstimateInfo(grossPayValues);
    setOtherLoadPayItemsInfo(otherPayValues);
    setOpenEstimateModal(true);
  };

  useEffect(() => {
    const newGrossPayEstimateData = { ...grossPayEstimateInfo };

    // If there is qbo_service_id or invoice_service_id, but at the time of edition
    // isDriverPayPercentageBasedOnDefaultService is false, we show "Calculate" button
    // and clear service ids values
    if (
      !isDriverPayPercentageBasedOnDefaultService &&
      (driver.driver_pay_override_data?.invoice_service_id ||
        driver.driver_pay_override_data?.qbo_service_id)
    ) {
      setGrossPayEstimateInfo({
        ...newGrossPayEstimateData,
        gross_pay_estimate: undefined,
        overriden_order_rate: show_advanced_rates ? totalLoadRates : stateRate,
        qbo_service_id: undefined,
        invoice_service_id: undefined,
        quantity: newGrossPayEstimateData.quantity || 0
      });
      onGrossPayEstimateDataSave({
        ...newGrossPayEstimateData,
        gross_pay_estimate: undefined,
        overriden_order_rate: show_advanced_rates ? totalLoadRates : stateRate,
        qbo_service_id: undefined,
        invoice_service_id: undefined,
        quantity: newGrossPayEstimateData.quantity || 0
      });
      setCalculate(false);
    } else {
      setCalculate(true);
    }
  }, [
    isDriverPayPercentageBasedOnDefaultService,
    driver.driver_pay_override_data?.qbo_service_id,
    driver.driver_pay_override_data?.invoice_service_id,
    stateRate
  ]);

  useEffect(() => {
    if (driver.driver_pay_override_data === undefined || driver.driver_pay_override_data === null) {
      setGrossPayEstimateInfo(undefined);
    }
  }, [driver.driver_pay_override_data]);

  useEffect(() => {
    const driverPayOverride = driver.driver_pay_override_data?.gross_pay_estimate || 0;
    const otherPayItemsTotalAmount = DriverPayUtils.getOtherPayItemsTotalAmount(
      driver.trip_driver_pay_other_payment || otherLoadPayItemsInfo
    );
    const totalDriverPayEstimate = driverPayOverride + otherPayItemsTotalAmount;
    setTotalDriverPayEstimate(totalDriverPayEstimate);
  }, [
    driver.driver_pay_override_data,
    driver.trip_driver_pay_other_payment,
    otherLoadPayItemsInfo
  ]);

  const hasNoPayData = (driver: ContactFormDriver): boolean => {
    const hasNoOtherPayments = !driver.trip_driver_pay_other_payment?.length;
    const hasNoOverrideData =
      driver.driver_pay_override_data === undefined || driver.driver_pay_override_data === null;
    return hasNoOtherPayments && hasNoOverrideData;
  };

  return (
    <>
      {hasNoPayData(driver) || !calculate ? (
        <>
          <Typography sx={{ fontWeight: '500' }} className="contact-details__instructions-left">
            {`Driver's pay estimate`}
          </Typography>
          <TwButton
            type={TwButtonType.secondary}
            label="Calculate"
            action={handleGrossPayCalculation}
            sx={{ width: '100px' }}
          ></TwButton>
        </>
      ) : (
        <Stack flexDirection="row">
          <TwFieldCustom
            inputType={TwInputTypes.number}
            label="Driver's pay estimate"
            name="drivers_estimate_pay"
            startAdorment={
              <Typography variant="body2" fontWeight="400" fontSize="14px" color="#6B7280">
                $
              </Typography>
            }
            endAdorment={
              <EditPencil
                sx={{ position: 'relative', left: '5px' }}
                onClick={handleOpenCalculatorModal}
              />
            }
            // We need to add the other pay items total amount to the driver pay override,
            // to simulate the sum which will be re-calculated in the BE when the load is saved
            value={
              !hasNoPayData(driver) && totalDriverPayEstimate
                ? parseFloat(`${totalDriverPayEstimate || 0}`).toFixed(2) || '0.00'
                : '0.00'
            }
            disabled
          />
          {driver.driver_pay_override_data && (
            <TwGrossPayTooltip
              numberOfDrivers={driversNumber}
              loadType={load_type}
              showLabel
              grossPayData={driver.driver_pay_override_data}
              otherPayItemsTotalAmount={DriverPayUtils.getOtherPayItemsTotalAmount(
                otherLoadPayItemsInfo
              )}
            />
          )}
        </Stack>
      )}

      <TwDialog
        onClickClose={closeModal}
        open={openEstimateModal}
        title={'Driver pay estimate'}
        classNameActions="contact-details__drivercalculator-dialog-actions"
        actions={[
          <TwButton
            key="1"
            type={TwButtonType.secondary}
            label="Reset"
            action={resetEstimateValues}
          />,
          <TwButton key="1" type={TwButtonType.secondary} label="Cancel" action={closeModal} />,
          <TwButton key="2" type={TwButtonType.primary} label="Save" action={handleSaveEstimate} />
        ]}
      >
        {driverAutocomplete &&
          (!driverAutocomplete.pay_rate || !driverAutocomplete.default_pay_rate_type) && (
            <DriverAlertStyled
              severity="info"
              icon={<InfoIcon sx={{ color: '#9CA3AF' }} />}
              className="tid-alert"
            >
              This driver is missing a settlement info.{' '}
              <TwButton
                type={TwButtonType.text}
                action={openEditDriverFromModal}
                label="Edit driver"
              />
            </DriverAlertStyled>
          )}
        {grossPayEstimateInfo && (
          <>
            <TwDriverPayRateTypeSelector
              id="pay-type"
              name="pay-type"
              label="Driver's pay rate type"
              value={
                DriverUtils.getPayTypeOption(grossPayEstimateInfo?.unit) ||
                DriverUtils.payRateTypesToAutocompleteOptions()[0]
              }
              onChange={(_, newValue: TwAutocompleteOption | null): void => {
                if (newValue) {
                  setGrossPayEstimateInfo((prev) => {
                    if (newValue.id === 'FLAT') {
                      return {
                        ...prev,
                        unit: newValue.id as TRIP_DRIVER_PAY_UNIT,
                        gross_pay_estimate: 0,
                        rate: 0
                      };
                    } else if (newValue.id === 'PERCENTAGE') {
                      const { overridenLoadRate, qboServiceId, invoiceServiceId } =
                        determinePercentageCalculation(true);
                      const rateToUse = overridenLoadRate;
                      return {
                        ...prev,
                        unit: newValue.id as TRIP_DRIVER_PAY_UNIT,
                        qbo_service_id: qboServiceId,
                        invoice_service_id: invoiceServiceId,
                        overriden_order_rate:
                          load_type === LOAD_TYPE.SIMPLE
                            ? (rateToUse || 0) / driversNumber
                            : rateToUse
                      };
                    } else {
                      return { ...prev, unit: newValue.id as TRIP_DRIVER_PAY_UNIT };
                    }
                  });
                  setResetValues(true);
                }
              }}
            />
            <CalculationBox
              entity={entity}
              isModalOpen={openEstimateModal}
              grossPayEstimateData={grossPayEstimateInfo}
              onGrossPayEstimateChange={(grossPay: TripDriverOverrideData): void => {
                setGrossPayEstimateInfo(grossPay);
              }}
              hasBeenReset={resetValues}
              handleResetValues={setResetValues}
              splitLoadSegment={splitLoadSegment}
              driversNumber={driversNumber}
              otherLoadPayItems={otherLoadPayItemsInfo}
              onOtherPayItemsChange={(otherPayItems: TripDriverOtherPay[]): void => {
                setOtherLoadPayItemsInfo(otherPayItems);
              }}
              otherPayItemsErrors={otherPayItemsErrors}
            />
          </>
        )}
      </TwDialog>
    </>
  );
};

export default TwDriverPayCalculator;
